import type { AxiosResponse } from 'axios';
import type { CandidateStatus } from 'services/candidates';
import type {
  BatchReadHandler,
  CampaignCandidateResponse,
  CampaignFiltersBody,
  CampaignResponse,
  ChangeOwnerHandler,
  CheckUnreadButtonAvailabilityHandler,
  CopyCampaignTemplatesHandler,
  CreateAtsSearchHandler,
  CreateCampaignHandler,
  CreateCustomLogHandler,
  CreateTextSearchHandler,
  DeleteCampaignCandidateNotesHandler,
  DownloadCampaignCandidateCVHandler,
  GenerateReachoutMessageHandler,
  GetCampaignCandidateCVHandler,
  GetCampaignCandidateCVURLHandler,
  GetCampaignCandidateHandler,
  GetCampaignCandidateNotesHandler,
  GetCampaignCandidatesStatsHandler,
  GetCampaignHandler,
  GetCampaignNewRepliesHandler,
  GetCampaignRatedCandidatesHandler,
  GetCampaignsHandler,
  GetCampaignsOverviewHandler,
  GetCampaignsPageHandler,
  GetCampaignTemplatesHandler,
  GetCandidateActivityLogHandler,
  GetCandidateHistoryHandler,
  GetCountCampaignsCandidatesHandler,
  GetCountCampaignsHandler,
  GetDistinctedLabelsHandler,
  GetFacetsHandler,
  GetFileHandler,
  GetHasMessagesHandler,
  GetInitialCampaignHandler,
  GetPublicVacancyHandler,
  GetRJActivityLogsHandler,
  GetRjBulkUsageHandler,
  GetSearchResultsHandler,
  ManageRJCommunicationHubPipelineHandler,
  CreateRjIndeedOutreachAttemptHandler,
  ConfirmRjIndeedOutreachAttemptHandler,
  MarkActivityLogAsReadBasedOnEsPersonIdHandler,
  MarkActivityLogAsReadHandler,
  MarkAsUnreadHandler,
  NotifyCandidateHandler,
  OptOutCommunicationHandler,
  PostApproveCampaignCandidateHandler,
  PostCampaignCandidateNotesHandler,
  PutCampaignCandidateNotesHandler,
  PutCampaignCandidateSaveLabelsHandler,
  SearchEsCampaignCandidateHandler,
  StartRJCommunicationHubPipelineHandler,
  StatusChangeFeedbackParams,
  SubmitCandidateFeedbackHandler,
  SubmitCandidateInterestHandler,
  TranslateMessageHandler,
  UpdateCampaignHandler,
  VectorSearchMatchingCandidateHandler,
  VectorSearchMatchingCandidatePayload,
} from './CampaignsClient.types';

import { BaseClient } from 'clients/BaseClient/BaseClient';
import { hosts } from 'config';
import type { IndeedRobinsearchCredentials } from 'shared/legacy/hooks/useBrowserExtension/indeed/indeed.types';

export class CampaignsClient extends BaseClient {
  constructor() {
    super();
    this.getFacets = this.getFacets.bind(this);
    this.createOrUpdateCampaignCandidate = this.createOrUpdateCampaignCandidate.bind(this);
    this.createCampaign = this.createCampaign.bind(this);
    this.updateCampaign = this.updateCampaign.bind(this);
    this.deleteCampaign = this.deleteCampaign.bind(this);
    this.createCampaignFilters = this.createCampaignFilters.bind(this);
    this.updateCampaignFilters = this.updateCampaignFilters.bind(this);
    this.getCampaignFilters = this.getCampaignFilters.bind(this);
    this.getCampaignCurrentFilters = this.getCampaignCurrentFilters.bind(this);
    this.getCampaignCandidate = this.getCampaignCandidate.bind(this);
    this.getPublicVacancy = this.getPublicVacancy.bind(this);
    this.submitCandidateFeedback = this.submitCandidateFeedback.bind(this);
    this.vectorSearchMatchingCandidate = this.vectorSearchMatchingCandidate.bind(this);
  }

  changeOwner: ChangeOwnerHandler = ({ campaignId, userId }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/change_owner/`,
      method: 'POST',
      data: {
        user_id: userId,
      },
    });
  };

  markActivityLogAsRead: MarkActivityLogAsReadHandler = ({ activity_log_id: activity_Log_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/activity_logs/${activity_Log_id}/`,
      method: 'PATCH',
      data: {
        is_new_message: false,
      },
    });
  };

  checkUnreadButtonAvailability: CheckUnreadButtonAvailabilityHandler = ({ es_person_id: es_person_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/activity_logs/check_unread_availability/`,
      method: 'POST',
      data: {
        es_person_id: es_person_id,
      },
    });
  };

  markActivityLogAsReadBasedOnEsPersonId: MarkActivityLogAsReadBasedOnEsPersonIdHandler = ({
    es_person_id: es_person_id,
  }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/activity_logs/mark_as_read/`,
      method: 'POST',
      data: {
        es_person_id: es_person_id,
      },
    });
  };

  markBatchAsRead: BatchReadHandler = () => {
    return this.request({
      url: `${hosts.campaignsApi()}/activity_logs/batch_mark_as_read_based_on_user/`,
      method: 'POST',
    });
  };

  markActivityLogAsUnreadBasedOnEsPerson: MarkAsUnreadHandler = ({ es_person_id, campaign_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/activity_logs/mark_or_create_unread_based_on_es_person/`,
      method: 'POST',
      data: {
        es_person_id: es_person_id,
        campaign_id: campaign_id,
      },
    });
  };

  getCampaigns: GetCampaignsHandler = (params) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/`,
      method: 'GET',
      params,
    });
  };

  getCountCampaigns: GetCountCampaignsHandler = (params) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/count_campaigns/`,
      method: 'GET',
      params,
    });
  };

  get20Campaigns: GetCampaignsPageHandler = (params) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/?limit=20`,
      method: 'GET',
      params,
    });
  };

  getCampaignsPage: GetCampaignsPageHandler = (params) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/`,
      method: 'GET',
      params,
    });
  };

  notifyCandidate: NotifyCandidateHandler = ({ campaign_id, candidate_id, send_method, data, attachments }) => {
    if (attachments) {
      const formData = new FormData();

      formData.append('data', JSON.stringify(data));
      attachments.forEach((file) => {
        formData.append('files', file);
      });

      return this.request({
        url: `${hosts.campaignsApi()}/campaigns/${campaign_id}/candidates/${candidate_id}/notifications/v2/${send_method}/`,
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }

    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaign_id}/candidates/${candidate_id}/notifications/${send_method}/`,
      method: 'POST',
      data,
    });
  };

  getFacets: GetFacetsHandler = (id) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${id}/search_facets/`,
      method: 'GET',
    });
  };

  getCampaignsOverview: GetCampaignsOverviewHandler = (params) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/campaigns_overview`,
      params,
      method: 'GET',
    });
  };

  getCampaignRatedCandidate: GetCampaignRatedCandidatesHandler = ({
    id,
    status,
    limit = 20,
    offset = 0,
    labels = [],
    sort: { by = 'updated_at', order = 'ASC' } = {},
    cancelToken,
  }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${id}/candidates/`,
      method: 'GET',
      params: {
        limit,
        offset,
        status,
        ordering: `${order === 'DESC' ? '-' : ''}${by}`,
        reasons: labels,
      },
      cancelToken,
    });
  };

  getCampaignCandidate: GetCampaignCandidateHandler = ({ campaignId, esPersonId }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/candidates/${esPersonId}/`,
      method: 'GET',
    });
  };

  getCountCampaignsCandidates: GetCountCampaignsCandidatesHandler = () => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/count_initial_candidates/`,
      method: 'GET',
    });
  };

  getCampaignCandidatesStats: GetCampaignCandidatesStatsHandler = (id) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${id}/candidates_stats/`,
      method: 'GET',
    });
  };

  async createOrUpdateCampaignCandidate({
    campaignId,
    esPersonId,
    status,
    reasons,
    is_unlocked,
    freeUnlock,
    status_change_feedback,
    skipContactedLogCreation = false,
  }: {
    campaignId: string | number;
    esPersonId: string | number;
    status: CandidateStatus;
    reasons?: string[];
    is_unlocked?: boolean;
    freeUnlock?: boolean;
    status_change_feedback?: StatusChangeFeedbackParams;
    skipContactedLogCreation?: boolean;
  }): Promise<AxiosResponse<CampaignCandidateResponse>> {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/candidates/`,
      method: 'POST',
      data: {
        es_person_id: esPersonId,
        is_unlocked,
        status,
        reasons,
        skip_contacted_log_creation: skipContactedLogCreation,
        ...(typeof freeUnlock !== 'undefined' && { free_unlock: freeUnlock }),
        ...(status_change_feedback && {
          status_change_feedback: status_change_feedback.map((feedback) => ({
            status,
            key: feedback.key,
            value: feedback.value,
          })),
        }),
      },
    });
  }

  getCampaign: GetCampaignHandler = (campaignId) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/`,
      method: 'GET',
    });
  };

  getInitialCampaign: GetInitialCampaignHandler = () => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/initial_campaign/`,
      method: 'GET',
    });
  };

  createCampaign: CreateCampaignHandler = (data) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/`,
      method: 'POST',
      data,
    });
  };

  updateCampaign: UpdateCampaignHandler = (campaignId, data) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/`,
      method: 'PATCH',
      data,
    });
  };

  async copyCampaign(campaignId: string | number): Promise<AxiosResponse<CampaignResponse>> {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/copy/`,
      method: 'POST',
    });
  }

  async deleteCampaign(campaignId: string | number) {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/`,
      method: 'DELETE',
    });
  }

  async createCampaignFilters(
    campaignId: string,
    data: CampaignFiltersBody,
  ): Promise<AxiosResponse<CampaignFiltersBody>> {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/filters/`,
      method: 'POST',
      data,
    });
  }

  async updateCampaignFilters(
    campaignId: string | number,
    filtersId: string | number,
    data: CampaignFiltersBody,
  ): Promise<AxiosResponse<CampaignFiltersBody>> {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/filters/${filtersId}/`,
      method: 'PUT',
      data,
    });
  }

  async getCampaignFilters(
    key: string,
    campaignId: string | number,
    filtersId: string | number,
  ): Promise<AxiosResponse<CampaignFiltersBody>> {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/filters/${filtersId}/`,
      method: 'GET',
    });
  }

  async getCampaignCurrentFilters(key: string, campaignId: string): Promise<AxiosResponse<CampaignFiltersBody>> {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/filters/current/`,
      method: 'GET',
    });
  }

  getSearchResults: GetSearchResultsHandler = ({ campaignId, limit = 20, offset = 0, cancelToken }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/search_batch/`,
      method: 'POST',
      params: { limit, offset },
      cancelToken,
    });
  };

  getCandidateActivityLog: GetCandidateActivityLogHandler = ({ candidate_id, limit = 10, offset = 0 }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/candidates/${candidate_id}/activity_logs?limit=${limit}&offset=${offset}`,
      method: 'GET',
    });
  };

  getCandidateHistory: GetCandidateHistoryHandler = ({ candidate_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/candidates/${candidate_id}/history/`,
      method: 'GET',
    });
  };

  getHasMessages: GetHasMessagesHandler = ({ es_person_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/candidates/${es_person_id}/history/has-messages/`,
      method: 'GET',
    });
  };

  getCampaignCandidateCV: GetCampaignCandidateCVHandler = ({ campaignId, candidateId, options }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/candidates/${candidateId}/cv_preview/`,
      method: 'GET',
      ...options,
    });
  };

  getFile: GetFileHandler = (url) => {
    return this.request({ url, method: 'GET', responseType: 'blob' }, true, 0, false);
  };

  getCampaignCandidateCVURL: GetCampaignCandidateCVURLHandler = ({ cvFileId, shouldUseRedact = false }) => {
    return this.request({
      url: `${hosts.cvApi()}/preview/${shouldUseRedact ? 'cv_url_redact' : 'cv_url'}/${cvFileId}/`,
      method: 'GET',
    });
  };

  getCampaignCandidateATSCV: GetCampaignCandidateCVHandler = ({ campaignId, candidateId, options }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/candidates/${candidateId}/cv_preview_ats/`,
      method: 'GET',
      ...options,
    });
  };

  downloadCampaignCandidateCV: DownloadCampaignCandidateCVHandler = (
    campaignId,
    candidateId,
    name,
    fileExtension = 'pdf',
  ) => {
    return this.fetchFile(`${name || 'CV'}.${fileExtension}`, {
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/candidates/${candidateId}/cv_preview/`,
      method: 'GET',
    });
  };

  postApproveCampaignCandidate: PostApproveCampaignCandidateHandler = ({ campaignId, candidateId }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/candidates/${candidateId}/post_approve_candidate/`,
      method: 'POST',
    });
  };

  async postRefreshCampaignCandidate(campaignId: string | number, esPersonId: number | string): Promise<any> {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaignId}/candidates/${esPersonId}/refresh_candidate/`,
      method: 'POST',
    });
  }

  getCampaignCandidateNotes: GetCampaignCandidateNotesHandler = ({ es_person_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/candidates/${es_person_id}/notes/`,
      method: 'GET',
    });
  };

  postCampaignCandidateNotes: PostCampaignCandidateNotesHandler = ({ es_person_id, notes, campaign_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/candidates/${es_person_id}/notes/`,
      data: { content: notes, campaign: campaign_id },
      method: 'POST',
    });
  };

  putCampaignCandidateNotes: PutCampaignCandidateNotesHandler = ({ es_person_id, note_id, notes, campaign_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/candidates/${es_person_id}/notes/${note_id}/`,
      data: { content: notes, campaign: campaign_id },
      method: 'PUT',
    });
  };

  deleteCampaignCandidateNotes: DeleteCampaignCandidateNotesHandler = ({ es_person_id, note_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/candidates/${es_person_id}/notes/${note_id}/`,
      method: 'DELETE',
    });
  };

  putCampaignCandidateLabels: PutCampaignCandidateSaveLabelsHandler = ({ es_person_id, campaign_id, labels }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaign_id}/candidates/${es_person_id}/labels/`,
      data: { reasons: labels },
      method: 'PUT',
    });
  };

  getDistinctedLabels: GetDistinctedLabelsHandler = () => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/candidates/labels/`,
      method: 'GET',
    });
  };

  getCampaignTemplates: GetCampaignTemplatesHandler = ({ search, limit = 200 }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaign_templates/?limit=${limit}&search=${search}`,
      method: 'GET',
    });
  };

  copyCampaignTemplates: CopyCampaignTemplatesHandler = ({ templateId }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaign_templates/${templateId}/copy/`,
      method: 'POST',
    });
  };

  createTextSearch: CreateTextSearchHandler = ({ job_description }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/create_through_text/`,
      method: 'POST',
      data: { job_description },
    });
  };

  createAtsSearch: CreateAtsSearchHandler = ({ ats_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/create_through_ats_vacancy/`,
      method: 'POST',
      data: { ats_id },
    });
  };

  generateReachoutMessage: GenerateReachoutMessageHandler = (data) => {
    return this.request({
      url: `${hosts.campaignsApi()}/outreach/generate_reachout_message`,
      method: 'POST',
      data,
    });
  };

  getCampaignNewReplies: GetCampaignNewRepliesHandler = () => {
    return this.request({
      url: `${hosts.campaignsApi()}/activity_logs/new_replies/`,
      method: 'GET',
    });
  };

  createCustomLog: CreateCustomLogHandler = ({
    es_person_id,
    source,
    campaign,
    contacted_channel,
    action,
    metadata,
  }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/activity_logs/create_custom_log/`,
      method: 'POST',
      data: {
        es_person_id,
        source,
        campaign,
        contacted_channel,
        action,
        ...(metadata != null && Object.keys(metadata).length > 0 ? { metadata } : {}),
      },
    });
  };

  searchEsCampaignCandidate: SearchEsCampaignCandidateHandler = ({ esPersonIds }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/search_candidates_by_es_person/`,
      method: 'POST',
      data: {
        es_person_ids: esPersonIds,
      },
    });
  };

  async getTenantRatedCandidatesStats(team?: boolean) {
    return this.request({
      url: `${hosts.campaignsApi()}/candidate_stats/`,
      method: 'GET',
      params: { team },
    });
  }

  startRJCommunicationHubPipeline: StartRJCommunicationHubPipelineHandler = ({
    campaign_id,
    es_person_id,
    ...data
  }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaign_id}/candidates/${es_person_id}/rj_communication_hub/start/`,
      method: 'POST',
      data,
    });
  };

  manageRJCommunicationHubPipeline: ManageRJCommunicationHubPipelineHandler = ({
    campaign_id,
    es_person_id,
    action,
  }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaign_id}/candidates/${es_person_id}/rj_communication_hub/${action}/`,
      method: 'POST',
    });
  };

  createRjIndeedOutreachAttempt: CreateRjIndeedOutreachAttemptHandler = ({ campaign_id, es_person_id, ...data }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaign_id}/candidates/${es_person_id}/rj_comm_hub/indeed/attempts/`,
      method: 'POST',
      data,
    });
  };

  confirmRjIndeedOutreachAttempt: ConfirmRjIndeedOutreachAttemptHandler = ({
    campaign_id,
    es_person_id,
    attempt_id,
    ...data
  }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/campaigns/${campaign_id}/candidates/${es_person_id}/rj_comm_hub/indeed/attempts/${attempt_id}/confirm_send/`,
      method: 'POST',
      data,
    });
  };

  submitCandidateInterest: SubmitCandidateInterestHandler = ({ token }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/r/${token}/`,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });
  };

  getRJActivityLogs: GetRJActivityLogsHandler = ({ es_person_id, campaign_id }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/rj_activity_logs/`,
      method: 'GET',
      params: {
        es_person_id,
        campaign_id,
      },
    });
  };

  getRjBulkUsage: GetRjBulkUsageHandler = () => {
    return this.request({
      url: `${hosts.campaignsApi()}/rj-communication/bulk-usage/`,
      method: 'GET',
    });
  };

  getPublicVacancy: GetPublicVacancyHandler = ({ token }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/public/vacancies_json/${token}/`,
      method: 'GET',
    });
  };

  submitCandidateFeedback: SubmitCandidateFeedbackHandler = ({
    token,
    feedback,
    robin_verified,
    communication_opt_out,
  }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/public/candidate/feedback/`,
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: {
        token,
        feedback,
        robin_verified,
        communication_opt_out,
      },
    });
  };

  optOutCommunication: OptOutCommunicationHandler = ({ es_person_id }) => {
    return this.request(
      {
        url: `${hosts.campaignsApi()}/candidate/opt-out/communication-agent/`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        data: {
          es_person_id,
        },
      },
      false,
    );
  };

  vectorSearchMatchingCandidate: VectorSearchMatchingCandidateHandler = (
    payload: VectorSearchMatchingCandidatePayload,
  ) => {
    return this.request({
      url: `${hosts.vectorSearchApi()}/vector_search_matching_candidate`,
      method: 'POST',
      data: payload,
    });
  };

  translateMessage: TranslateMessageHandler = ({ message, language, subject }) => {
    return this.request({
      url: `${hosts.campaignsApi()}/candidates/history/translate_message/`,
      method: 'POST',
      data: {
        message,
        language,
        ...(subject && { subject }),
      },
    });
  };

  async fetchIndeedCredentials(): Promise<IndeedRobinsearchCredentials | null> {
    try {
      const response = await this.request<IndeedRobinsearchCredentials | null>({
        url: `${hosts.campaignsApi()}/platforms-credentials/`,
        method: 'GET',
      });

      return response.data ?? null;
    } catch (error) {
      return null;
    }
  }

  saveIndeedCredentials(payload: {
    ctk: string;
    username: string;
    status: string;
    platform: string;
    api_key: string;
    csrf_token: string;
  }): void {
    void this.request({
      url: `${hosts.campaignsApi()}/platforms-credentials/`,
      method: 'POST',
      data: payload,
    }).catch(() => {
      // fire-and-forget
    });
  }
}

export const campaignsClient = new CampaignsClient();
